import type { ScheduleValues } from '../types/notificationTest';
import type {
  AppPushContentValues,
  AppPushFormValues,
  AppPushPayload,
} from '../types/appPushTest';
import type {
  KeyValueEntry,
  WebPushSchedulePayload,
} from '../types/webPushTest';

const pad = (n: number): string => String(n).padStart(2, '0');

const toLocalIso = (date: string, time: string): string => {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
};

const flattenKv = (entries: KeyValueEntry[]): Record<string, string> =>
  entries
    .filter((v) => v.key && v.key.trim() !== '')
    .reduce<Record<string, string>>((acc, v) => {
      acc[v.key] = v.value;
      return acc;
    }, {});

const buildSchedule = (
  s: ScheduleValues
): WebPushSchedulePayload | undefined => {
  if (s.type === 'NONE') return undefined;
  const result: WebPushSchedulePayload = {
    type: s.type,
    timezone: s.timezone,
    recurring: {
      interval: {
        value: Number(s.recurring.intervalValue) || 0,
        unit: s.recurring.intervalUnit,
      },
    },
  };
  if (s.date && s.time) {
    result.datetime = toLocalIso(s.date, s.time);
  }
  if (s.recurring.expiresDate && s.recurring.expiresTime) {
    result.recurring.expiresAt = toLocalIso(
      s.recurring.expiresDate,
      s.recurring.expiresTime
    );
  }
  return result;
};

const buildContent = (
  c: AppPushContentValues
): AppPushPayload['notification']['content'] => {
  if (c.type === 'TEMPLATE') {
    return {
      type: 'TEMPLATE',
      templateId: c.templateId,
      templateType: c.templateType,
      templateReturnType: c.templateReturnType,
      language: c.language,
      variables: flattenKv(c.variables),
    };
  }
  return {
    type: 'CUSTOM',
    title: c.title,
    message: c.message,
  };
};

export const buildAppPushPayload = (v: AppPushFormValues): AppPushPayload => {
  const schedule = buildSchedule(v.schedule);
  return {
    accountId: v.accountId,
    appId: v.appId,
    groupIds: v.groupIds,
    notification: {
      type: v.notification.type,
      priority: v.notification.priority,
      data: flattenKv(v.notification.data),
      actions: v.notification.actions,
      content: buildContent(v.content),
      ...(schedule ? { schedule } : {}),
    },
  };
};
